import {
  Fragment,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
  type RefObject,
} from "react";
import { X } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { cn } from "@/lib/utils";
import type { FilterChip } from "./filterChip";

/**
 * Fallback gap between chips for environments where computed styles carry no
 * layout (jsdom). In the browser the real value is read off the mirror's
 * computed `column-gap`, so it tracks the root font scale automatically.
 */
const CHIP_GAP = 8;

interface FilterChipsProps<K extends string> {
  /** Active chips in dimension order; this component re-orders them by age. */
  chips: FilterChip<K>[];
  /** Reopen the filter popover on a chip's dimension. */
  onOpenDimension: (dimension: K) => void;
  /** Omit when the Filters button already carries its own clear control. */
  onClearAll?: () => void;
  /** Copy overrides; the shared `common.filterPanel` copy otherwise. */
  removeLabel?: string;
  clearAllLabel?: string;
  /** "+{count} more" */
  moreLabel?: (count: number) => string;
  /** An icon for one value in the "+N more" list — a status colour, a profile picture. */
  valueIcon?: ValueIcon<K>;
  className?: string;
}

type ValueIcon<K extends string> = (dimension: K, valueId: string) => ReactNode;

/**
 * Sub-pixel slack for the fit test. Chip widths are read as whole pixels
 * (offsetWidth) and the row as a fractional box, so a row that exactly holds
 * its chips can measure a pixel short and collapse one for nothing.
 */
const FIT_SLACK_PX = 2;

/**
 * Keeps the order in which filters were switched on, so the newest chip can sit
 * closest to the buttons and the oldest fall off the left end first. Derived
 * from the live chip set: a filter that is cleared drops out, and switching it
 * back on re-enters as the newest. The order is adjusted during render (React's
 * "adjusting state when a prop changes" escape hatch) so the very first frame
 * after a filter change is already ordered — no reshuffle after paint.
 */
function useInsertionOrder<K extends string>(keys: K[]): K[] {
  const [seen, setSeen] = useState<K[]>(keys);
  const kept = seen.filter((k) => keys.includes(k));
  const merged = [...kept, ...keys.filter((k) => !kept.includes(k))];
  const changed = merged.length !== seen.length || merged.some((k, i) => seen[i] !== k);
  if (changed) setSeen(merged);
  return merged;
}

/**
 * How many of the newest chips fit on one line, measured against the invisible
 * mirror row (a collapsed chip can't be measured any more, so the mirror always
 * holds them all). Everything that doesn't fit hides behind the "+N more" pill.
 */
function useVisibleChipCount<K extends string>(
  rowRef: RefObject<HTMLDivElement>,
  mirrorRef: RefObject<HTMLDivElement>,
  ordered: FilterChip<K>[],
  hasClearAll: boolean
): number {
  const [visibleCount, setVisibleCount] = useState(ordered.length);
  const signature = ordered.map((c) => c.label).join("|");

  useLayoutEffect(() => {
    const row = rowRef.current;
    const mirror = mirrorRef.current;
    if (!row || !mirror) return;

    const measure = () => {
      const rowWidth = row.clientWidth;
      const gap = Number.parseFloat(getComputedStyle(mirror).columnGap) || CHIP_GAP;
      const parts = Array.from(mirror.children) as HTMLElement[];
      // mirror layout: [...chips, clearAll?, ...pills] — pills[k] reads "+{k+1} more"
      const chipWidths = parts.slice(0, ordered.length).map((el) => el.offsetWidth);
      const clearAllWidth = hasClearAll ? parts[ordered.length].offsetWidth : 0;
      const pillStart = ordered.length + (hasClearAll ? 1 : 0);
      const pillWidths = parts.slice(pillStart).map((el) => el.offsetWidth);

      // Nothing laid out to measure (jsdom, or the row hasn't been laid out
      // yet) — show everything rather than collapsing chips that would fit.
      if (rowWidth === 0 || chipWidths.some((w) => w === 0)) {
        setVisibleCount(ordered.length);
        return;
      }

      // A line of k items has k−1 gaps. Chips + Clear all is n+1 items, so the
      // sums below carry one gap per *chip* (n of them) and none for Clear all;
      // in the split loop the pill's gap stands in for Clear all's.
      const budget = rowWidth - clearAllWidth;
      const needed = chipWidths.reduce((sum, w) => sum + w + gap, 0);
      if (needed <= budget + FIT_SLACK_PX) {
        setVisibleCount(ordered.length);
        return;
      }

      // Doesn't fit: keep the newest chips, collapse the oldest behind the
      // pill. Each candidate split is measured against the pill at the count
      // it would actually show — a "+1 more" pill is narrower than "+4 more",
      // and over-reserving would needlessly collapse a chip that fits.
      for (let fits = chipWidths.length - 1; fits > 0; fits--) {
        let used = pillWidths[chipWidths.length - fits - 1] + gap;
        for (let i = chipWidths.length - fits; i < chipWidths.length; i++) {
          used += chipWidths[i] + gap;
        }
        if (used <= budget + FIT_SLACK_PX) {
          setVisibleCount(fits);
          return;
        }
      }
      setVisibleCount(0);
    };

    measure();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(measure);
    observer.observe(row);
    // The mirror too: chip widths can change without the row resizing (webfont
    // swap, browser text-size change) — the mirror is content-sized, so any
    // such change resizes it and re-runs the split.
    observer.observe(mirror);
    return () => observer.disconnect();
  }, [rowRef, mirrorRef, signature, ordered.length, hasClearAll]);

  return visibleCount;
}

/** One active filter: click the label to reopen its dimension, the ✕ to drop it. */
function FilterChipPill<K extends string>({
  chip,
  removeLabel,
  onOpen,
}: {
  chip: FilterChip<K>;
  removeLabel: string;
  onOpen: (dimension: K) => void;
}) {
  return (
    <span className="inline-flex shrink-0 items-center gap-1 rounded-full border bg-muted/40 py-0.5 pl-2 pr-1 text-xs">
      <button
        type="button"
        onClick={() => onOpen(chip.key)}
        className="flex min-w-0 items-center gap-1 hover:text-foreground"
      >
        {/* Only the value truncates. The "+N" is a shrink-0 sibling, so a long
            first value (a nameless user shows as their e-mail) eats into
            itself rather than swallowing the count of the others. */}
        <span className="max-w-[200px] truncate">{chip.text}</span>
        {chip.more && <span className="shrink-0 tabular-nums">{chip.more}</span>}
      </button>
      <button
        type="button"
        onClick={chip.onRemove}
        aria-label={`${removeLabel} ${chip.label}`}
        className="rounded-full p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground"
      >
        <X className="h-3 w-3" />
      </button>
    </span>
  );
}

/** The chips of one group, laid out left to right. */
function ChipRow<K extends string>({
  chips,
  removeLabel,
  onOpen,
}: {
  chips: FilterChip<K>[];
  removeLabel: string;
  onOpen: (dimension: K) => void;
}) {
  return (
    <>
      {chips.map((chip) => (
        <FilterChipPill key={chip.key} chip={chip} removeLabel={removeLabel} onOpen={onOpen} />
      ))}
    </>
  );
}

/** Drops every active filter at once. Shown from the first chip onwards. */
function ClearAllButton({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex shrink-0 items-center gap-1 text-xs text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
    >
      {label}
      <X className="h-3 w-3" />
    </button>
  );
}

/**
 * One collapsed filter as a line of the "+N more" list: the filter type in
 * bold, every selected value (with its icon where the host draws one) and a ✕.
 * Plain text, not a pill — the list is already the container.
 */
function OverflowLine<K extends string>({
  chip,
  removeLabel,
  onOpen,
  valueIcon,
}: {
  chip: FilterChip<K>;
  removeLabel: string;
  onOpen: (dimension: K) => void;
  valueIcon?: ValueIcon<K>;
}) {
  const spoken =
    chip.prefix && chip.values
      ? `${chip.prefix}: ${chip.values.map((v) => v.label).join(", ")}`
      : chip.label;
  return (
    <li className="flex items-center gap-2 rounded px-2 py-1 text-xs hover:bg-muted/60">
      <button
        type="button"
        onClick={() => onOpen(chip.key)}
        // The visible parts are separate spans; spell the line out in one.
        aria-label={spoken}
        className="flex min-w-0 flex-1 flex-wrap items-center gap-x-1 gap-y-0.5 text-left"
      >
        {chip.prefix && chip.values ? (
          <>
            <span className="font-semibold">{chip.prefix}:</span>
            {chip.values.map((value, i) => (
              <Fragment key={value.id ?? `${value.label}-${i}`}>
                <span className="inline-flex min-w-0 items-center gap-1">
                  {value.id && valueIcon?.(chip.key, value.id)}
                  <span className="truncate">{value.label}</span>
                </span>
                {i < chip.values!.length - 1 && <span aria-hidden="true">,</span>}
              </Fragment>
            ))}
          </>
        ) : (
          <span className="truncate">{chip.label}</span>
        )}
      </button>
      <button
        type="button"
        onClick={chip.onRemove}
        aria-label={`${removeLabel} ${chip.label}`}
        className="shrink-0 rounded p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground"
      >
        <X className="h-3 w-3" />
      </button>
    </li>
  );
}

/** The collapsed oldest chips, revealed on click at the left end of the group. */
function OverflowChips<K extends string>({
  chips,
  label,
  removeLabel,
  onOpen,
  valueIcon,
  onDismissedWhileOpen,
}: {
  chips: FilterChip<K>[];
  label: string;
  removeLabel: string;
  onOpen: (dimension: K) => void;
  valueIcon?: ValueIcon<K>;
  /** The pill unmounted with its popover open — the remeasure promoted the
      last hidden chip into the live row mid-interaction. */
  onDismissedWhileOpen: () => void;
}) {
  const [open, setOpen] = useState(false);
  // Mirrors `open` for the unmount cleanup below, which must not re-arm on
  // every toggle — so it can't read `open` directly, and a ref may only be
  // written outside render.
  const openRef = useRef(false);
  useEffect(() => {
    openRef.current = open;
  }, [open]);
  useEffect(
    () => () => {
      // Removing the last hidden chip unmounts pill and popover together, so
      // Radix has no trigger left to restore focus to and it drops to <body>,
      // restarting tab order at the top of the page. Hand focus to the live
      // row instead. The activeElement guard keeps a normal close (focus back
      // on the trigger, or on whatever was clicked outside) untouched.
      if (openRef.current && document.activeElement === document.body) {
        onDismissedWhileOpen();
      }
    },
    [onDismissedWhileOpen]
  );
  return (
    // A popover, not a menu: a Radix menu roves focus over its items only,
    // which left the ✕ buttons unreachable from the keyboard. The popover
    // keeps normal tab order, so both controls of each line work.
    // Non-modal (the default) because removing the last hidden chip unmounts
    // this while it is open, and a line's label opens the filter popover —
    // neither may happen under a modal's body pointer-events/aria-hidden lock.
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="inline-flex shrink-0 items-center rounded-full border bg-muted/40 px-2 py-0.5 text-xs hover:bg-muted"
        >
          {label}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-auto min-w-56 max-w-sm p-1">
        <ul className="flex flex-col">
          {chips.map((chip) => (
            <OverflowLine
              key={chip.key}
              chip={chip}
              removeLabel={removeLabel}
              valueIcon={valueIcon}
              onOpen={(dimension) => {
                // Hand off to the filter popover — leaving this one open would
                // stack the two.
                setOpen(false);
                onOpen(dimension);
              }}
            />
          ))}
        </ul>
      </PopoverContent>
    </Popover>
  );
}

/**
 * What `useVisibleChipCount` measures against: every chip at its natural
 * width, Clear all, and a "+N more" pill at every possible count — laid out
 * but never painted and never hit-testable. A collapsed chip can no longer be
 * measured, so the mirror always holds them all.
 */
function MeasuringMirror<K extends string>({
  mirrorRef,
  ordered,
  removeLabel,
  onOpen,
  clearAllLabel,
  onClearAll,
  moreLabel,
}: {
  mirrorRef: RefObject<HTMLDivElement>;
  ordered: FilterChip<K>[];
  removeLabel: string;
  onOpen: (dimension: K) => void;
  clearAllLabel: string;
  onClearAll?: () => void;
  moreLabel: (count: number) => string;
}) {
  return (
    <div
      ref={mirrorRef}
      aria-hidden="true"
      className="pointer-events-none invisible absolute left-0 top-0 flex items-center gap-2"
    >
      <ChipRow chips={ordered} removeLabel={removeLabel} onOpen={onOpen} />
      {onClearAll && <ClearAllButton label={clearAllLabel} onClick={onClearAll} />}
      {ordered.map((_, i) => (
        <span
          key={i}
          className="inline-flex shrink-0 items-center rounded-full border px-2 py-0.5 text-xs"
        >
          {moreLabel(i + 1)}
        </span>
      ))}
    </div>
  );
}

/**
 * The active-filter chips every page shows beside its Filters button, so a
 * filter can be dropped without opening the panel. First built for the issues
 * toolbar.
 *
 * They live to the LEFT of the toolbar's buttons and grow
 * leftward into the empty header space, so adding or removing a filter never
 * shifts a button. Newest chip sits nearest the buttons; "Clear all" caps the
 * group on the right. When the chips would outgrow the space, the oldest ones
 * collapse into a "+N more" pill at the left end that reveals them on click —
 * the row stays one line and the buttons stay put in every state.
 */
export function FilterChips<K extends string>({
  chips,
  onOpenDimension,
  onClearAll,
  removeLabel: removeLabelProp,
  clearAllLabel: clearAllLabelProp,
  moreLabel: moreLabelProp,
  valueIcon,
  className,
}: FilterChipsProps<K>) {
  const { t } = useLanguage();
  const copy = t.common.filterPanel;
  const removeLabel = removeLabelProp ?? copy.remove;
  const clearAllLabel = clearAllLabelProp ?? copy.clearAll;
  const moreLabel =
    moreLabelProp ?? ((count: number) => copy.more.replace("{count}", String(count)));
  const order = useInsertionOrder(chips.map((c) => c.key));
  const ordered = order
    .map((key) => chips.find((c) => c.key === key))
    .filter((c): c is FilterChip<K> => !!c);

  const rowRef = useRef<HTMLDivElement>(null);
  const mirrorRef = useRef<HTMLDivElement>(null);
  const visibleCount = useVisibleChipCount(rowRef, mirrorRef, ordered, Boolean(onClearAll));

  // First live control in the row: the oldest visible chip's label — which is
  // exactly the chip just promoted out of the pill — or, failing that, Clear
  // all. The mirror renders as a <div>, so `:scope > span`/`:scope > button`
  // never match inside it. Stable so `OverflowChips`' unmount cleanup does not
  // re-arm on every render. (ponytail: DOM query over threading a chip ref)
  const focusLiveRow = useCallback(() => {
    rowRef.current?.querySelector<HTMLElement>(":scope > span > button, :scope > button")?.focus();
  }, []);

  const shown = ordered.slice(Math.max(0, ordered.length - visibleCount));
  const hidden = ordered.slice(0, Math.max(0, ordered.length - visibleCount));
  const row = { removeLabel, onOpen: onOpenDimension };

  return (
    <div
      ref={rowRef}
      className={cn(
        // overflow-hidden keeps the measuring mirror (and the pre-measurement
        // first paint) from bleeding out of the header.
        "relative flex min-w-0 flex-1 items-center justify-end gap-2 overflow-hidden",
        className
      )}
    >
      <MeasuringMirror
        mirrorRef={mirrorRef}
        ordered={ordered}
        clearAllLabel={clearAllLabel}
        onClearAll={onClearAll}
        moreLabel={moreLabel}
        {...row}
      />

      {ordered.length > 0 && (
        <>
          {hidden.length > 0 && (
            <OverflowChips
              chips={hidden}
              label={moreLabel(hidden.length)}
              valueIcon={valueIcon}
              onDismissedWhileOpen={focusLiveRow}
              {...row}
            />
          )}
          <ChipRow chips={shown} {...row} />
          {onClearAll && <ClearAllButton label={clearAllLabel} onClick={onClearAll} />}
        </>
      )}
    </div>
  );
}
